// The organization the user is currently acting as.
//
// Scout and IDX share one Auth0 tenant, one client and one API audience, so the
// token alone cannot say which organization a request is for - a user may belong
// to several, and to different ones in each product. Every request therefore
// carries the choice explicitly, in the X-Vectech-Org header, and the backend
// validates it against the memberships in the token.
//
// Stored per browser rather than per session so a refresh does not silently
// bounce the user back to their default organization.
//
// Returning undefined is a valid, normal state: it means "no explicit choice",
// and the backend then resolves the user's own organization exactly as it always
// has. That is what makes sending this header a no-op for anyone in a single
// organization, and what lets it ship before an organization selector exists.
//
// Kept in step with agamotto-web's src/utils/activeOrg.js - the two apps share no
// package, and the header name and storage key must not drift.

// The product this app is. Membership is scoped per product - a user can be in
// different organizations in IDX and Scout - so the stored selection is too, or
// the two apps would overwrite each other's choice in the same browser.
export const PRODUCT = "Scout";

const STORAGE_KEY = `vectech.${PRODUCT}.activeOrg`;

export const ORG_HEADER = "X-Vectech-Org";

/**
 * Every organization at once, for the superuser panel's "All" view.
 *
 * Passed wherever a query takes an `org`, so it lands in RTK Query's cache key
 * like any single organization would. It is never sent as the org header: the
 * request layer turns it into `all_orgs=true`, which the backend honours for
 * superusers only and only on /devices, /trap-sites and /alerts-with-filters.
 * Any other endpoint ignores the parameter and answers for the operator's own
 * organization, so do not hand this to one.
 */
export const ALL_ORGS = "*";

// localStorage throws outright in some privacy modes rather than returning null,
// so every access is guarded. Losing the selection is recoverable; a crash in the
// request layer is not.
export const getActiveOrg = (): string | undefined => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value && value.trim() ? value.trim() : undefined;
  } catch (e) {
    return undefined;
  }
};

export const setActiveOrg = (org?: string | null): void => {
  try {
    if (org && String(org).trim()) {
      window.localStorage.setItem(STORAGE_KEY, String(org).trim());
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    // Ignored: the selection simply will not persist across a refresh.
  }
};

export const clearActiveOrg = (): void => setActiveOrg(undefined);

/**
 * The org header, or an empty object when no organization has been chosen.
 *
 * Spread into a request's headers. Deliberately omits the header rather than
 * sending an empty one - an empty value would be a claim ("act as no
 * organization") rather than the absence of one.
 */
export const activeOrgHeader = (): Record<string, string> => {
  const org = getActiveOrg();
  return org ? { [ORG_HEADER]: org } : {};
};

// The Auth0 rule puts the whole app_metadata object on the ID token as well as
// the access token, so memberships are readable client-side with no request.
//
// That matters more than it looks: the selection has to be reconciled BEFORE the
// first API call, because a stale organization would be refused with a 403 and
// the endpoint that would tell us the valid list is itself refused. Reading the
// token breaks that deadlock.
export const APP_METADATA_CLAIM = "https://mos-id.com/app_metadata";
export const USER_METADATA_CLAIM = "https://mos-id.com/user_metadata";

export interface Membership {
  org: string;
  role: string;
}

/** Superusers may act as any organization - a server-side privilege. */
export const readSuperUser = (user: any): boolean =>
  !!user?.[USER_METADATA_CLAIM]?.superUser;

/**
 * The caller's memberships for THIS product, from the Auth0 user object.
 *
 * An entry with no product is treated as granting every product, matching the
 * backend - users backfilled before the field existed must not lose access.
 */
export const readMembershipsForProduct = (user: any): Membership[] => {
  const raw = user?.[APP_METADATA_CLAIM]?.memberships;
  if (!Array.isArray(raw)) return [];
  const wanted = PRODUCT.toLowerCase();
  return raw
    .filter((m: any) => m && typeof m.org === "string" && m.org.trim())
    .filter(
      (m: any) => !m.product || String(m.product).toLowerCase() === wanted,
    )
    .map((m: any) => ({
      org: m.org.trim(),
      role:
        typeof m.role === "string" && m.role.trim() ? m.role.trim() : "member",
    }))
    .filter(
      (m: Membership, i: number, all: Membership[]) =>
        all.findIndex((o) => o.org === m.org) === i,
    );
};

/**
 * Bring the stored selection in line with what the user may actually act as.
 *
 * Empty, or naming an organization they are not in, becomes the first of their
 * memberships. No memberships for this product becomes empty - which is not a
 * failure, it is what an orgless or IDX-only user legitimately is, and the
 * backend then answers from the token alone as it always did.
 *
 * A superuser is exempt from the "must be one of yours" rule, because they may
 * act as any organization. Without that exemption their selection would be
 * reverted by the reload that switching performs - they would pick another
 * tenant, the page would reload, and reconcile would put them straight back on
 * their own membership. They still get a default when nothing is stored.
 *
 * Returns the organization now in effect, or undefined.
 */
export const reconcileActiveOrg = (
  memberships: Membership[] | undefined | null,
  { superUser = false }: { superUser?: boolean } = {},
): string | undefined => {
  const orgs = (memberships || []).map((m) => m.org);
  const current = getActiveOrg();

  if (superUser && current) return current;

  if (orgs.length === 0) {
    clearActiveOrg();
    return undefined;
  }

  if (current && orgs.includes(current)) return current;

  setActiveOrg(orgs[0]);
  return orgs[0];
};

/**
 * Whether this user's token lets them act as any organization in Scout.
 *
 * False is the token the backend refuses on every request with ORG_MISSING - most
 * often one minted before the user was granted a membership, and kept since in the
 * localstorage cache. AuthLayout does not mount the app for it.
 *
 * Mirrors the backend's readMemberships: a membership for this product, else the
 * legacy user_metadata.organization it still falls back to. A superuser counts
 * once they have chosen an organization, because that choice travels as the
 * header the backend accepts from them for any organization.
 */
export const hasProductAccess = (user: any): boolean => {
  if (readMembershipsForProduct(user).length > 0) return true;
  const legacy = user?.[USER_METADATA_CLAIM]?.organization;
  if (typeof legacy === "string" && legacy.trim()) return true;
  return readSuperUser(user) && !!getActiveOrg();
};
